import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config';

// Create axios instance
export const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Token storage helpers
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing to prevent infinite loops
let isRefreshing = false;

// Response interceptor - handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Prevent infinite retry loops
      if (isRefreshing) {
        tokenStorage.clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Try to refresh token
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) {
        try {
          isRefreshing = true;
          originalRequest._retry = true;

          const response = await axios.post(
            `${config.apiUrl}/api/v1/auth/token/refresh/`,
            { refresh: refreshToken }
          );

          // API wraps response in {success, data: {access, refresh}}
          const responseData = response.data.data || response.data;
          const { access, refresh: newRefresh } = responseData;

          // Store new tokens (refresh token rotates)
          tokenStorage.setTokens(access, newRefresh || refreshToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access}`;
          }
          return apiClient(originalRequest);
        } catch {
          // Refresh failed, clear tokens and redirect to login
          tokenStorage.clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token, redirect to login
      tokenStorage.clearTokens();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Helper to extract error message
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ 
      message?: string; 
      detail?: string;
      error?: { message?: string };
    }>;
    const data = axiosError.response?.data;
    
    // Check nested error.message first (our API format)
    if (data?.error?.message) {
      let msg = data.error.message;
      // Clean up prefix
      msg = msg.replace(/^Unexpected Error:\s*/i, '');
      msg = msg.replace(/^API Error:\s*/i, '');
      return msg;
    }
    
    return (
      data?.message ||
      data?.detail ||
      axiosError.message ||
      'An error occurred'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
